import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from '../../../redux/hooks';
import {
  toggleChartSettingsTopBreakdownsAction,
  toggleTopChartSettingsSlicesAction,
} from '../../../redux/slices/analyses';
import BuilderNumericalMenu from '../../Builder/BuilderFields/BuilderNumericalMenu';
import { NumberInputField } from '../../Form';
import { CHART_GROUPS_MAX_COUNT } from '../constants/utils';
import { useChartContext } from '../ChartContext';
import { theme } from '../../../styles/theme';
import { TextButton } from '../../Button';

interface IProps {
  analysisId: string;
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 400;
`;

const StyledTextButton = styled(TextButton)`
  font-weight: 500;
  color: ${theme.colors.mainBlue};
`;

const options = [
  {
    text: 'Top 10',
    value: 10,
  },
  {
    text: 'Top 15',
    value: 15,
  },
  {
    text: 'Top 20',
    value: 20,
  },
  {
    text: 'Top 25',
    value: 25,
  },
  {
    text: 'Top 30',
    value: 30,
  },
];

const ChartTopBreakdownSelector = ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    settings: { chartSettings },
    displayedSlices,
  } = useChartContext();

  const onChange = (newValue: number) => {
    dispatch(
      toggleChartSettingsTopBreakdownsAction({
        analysisId,
        topBreakdownsValue: newValue,
      }),
    );
    dispatch(
      toggleTopChartSettingsSlicesAction({
        analysisId,
        sliceIndex: newValue,
      }),
    );
  };

  const isUsingTop = useMemo(() => {
    const max = chartSettings.topBreakdownsValue;

    return (
      displayedSlices.slice(0, max).every((slice) => slice.isVisible) &&
      displayedSlices.slice(max).every((slice) => !slice.isVisible)
    );
  }, [displayedSlices, chartSettings]);

  const visibleSlices = displayedSlices.filter((slice) => slice.isVisible);

  if (isUsingTop) {
    return (
      <Container>
        <Label>Breakdown by Top</Label>
        <BuilderNumericalMenu
          trigger={
            <NumberInputField
              prefix={'Top'}
              value={chartSettings.topBreakdownsValue}
              readOnly
            />
          }
          options={options}
          value={chartSettings.topBreakdownsValue}
          onChange={onChange}
          max={CHART_GROUPS_MAX_COUNT}
          placeholder="Enter a value"
        />
      </Container>
    );
  }

  return (
    <Container>
      <Label>Breakdown with {visibleSlices.length} selected</Label>
      <StyledTextButton
        type="button"
        onClick={() => onChange(chartSettings.topBreakdownsValue!)}
      >
        {`Reset to Top ${chartSettings.topBreakdownsValue}`}
      </StyledTextButton>
    </Container>
  );
};

export default ChartTopBreakdownSelector;
